import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dahc20jp14a1h3mr6i70';
const MAX_LEN = 500;

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFeedback('提交成功，我们会尽快回复你。');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    setStatus('submitting');
    setFeedback('');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setFeedback('提交成功，我们会尽快回复你。');
        form.reset();
      } else {
        setStatus('error');
        setFeedback(
          typeof serverMsg === 'string' && serverMsg.trim() ? serverMsg : '提交失败，请稍后重试。',
        );
      }
    } catch {
      setStatus('error');
      setFeedback('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="guatian-contact-form"
      className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-8"
    >
      <h3 className="font-heading text-xl md:text-2xl text-foreground-950">给我们留言</h3>
      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
        对内容、活动规则或合作有想法，都可以通过这个表单告诉我们。
      </p>

      <input
        type="text"
        name="website_alt"
        className="form-anti-spam-check"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-xs text-foreground-600 mb-1.5">昵称</span>
          <input
            type="text"
            name="name"
            required
            maxLength={30}
            placeholder="怎么称呼你"
            className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
          />
        </label>

        <label className="block">
          <span className="block text-xs text-foreground-600 mb-1.5">邮箱</span>
          <input
            type="email"
            name="email"
            required
            maxLength={60}
            placeholder="方便我们回复你"
            className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
          />
        </label>

        <label className="block sm:col-span-2">
          <span className="block text-xs text-foreground-600 mb-1.5">留言类型</span>
          <select
            name="topic"
            required
            defaultValue=""
            className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
          >
            <option value="" disabled>
              请选择一个类型
            </option>
            <option value="内容建议">内容建议</option>
            <option value="活动规则疑问">活动规则疑问</option>
            <option value="话题投稿">话题投稿</option>
            <option value="合作沟通">合作沟通</option>
          </select>
        </label>

        <label className="block sm:col-span-2">
          <span className="block text-xs text-foreground-600 mb-1.5">留言内容</span>
          <textarea
            name="message"
            required
            rows={4}
            maxLength={MAX_LEN}
            placeholder={`请尽量描述清楚，最多 ${MAX_LEN} 字`}
            className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 resize-none focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
          ></textarea>
        </label>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <span className="text-xs text-foreground-500">留言最多 {MAX_LEN} 字</span>
        <div className="flex items-center gap-3">
          {feedback && (
            <span className={`text-xs ${status === 'error' ? 'text-primary-700' : 'text-secondary-700'}`}>
              {feedback}
            </span>
          )}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? '提交中…' : '提交留言'}
          </button>
        </div>
      </div>
    </form>
  );
}